import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { auth, setAuthToken } from '../api';
import type { User } from '../types/api';

interface AuthContextValue {
  user: User | null;
  token: string | null;
  loading: boolean;
  isAuthenticated: boolean;
  currentOrgId: string;
  setCurrentOrgId: (orgId: string) => void;
  login: (email: string, password: string) => Promise<User>;
  register: (name: string, email: string, password: string) => Promise<User>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentOrgId, setCurrentOrgIdState] = useState<string>(
    () => localStorage.getItem('bidforge-org-id') || ''
  );

  const setCurrentOrgId = (orgId: string) => {
    localStorage.setItem('bidforge-org-id', orgId);
    setCurrentOrgIdState(orgId);
  };

  useEffect(() => {
    // Check session via /me — works with cookie OR Authorization header.
    // The api interceptor attaches the persisted token (Safari fallback)
    // automatically, so this restores sessions where cookies were dropped
    // by ITP.
    auth.me()
      .then(async (res) => {
        setUser(res.data);
        // After successful me() call, get a fresh token for WS use + refresh
        // the persisted token so subsequent navigations stay authenticated.
        // 2026-05-10: skip if we refreshed within the last 5 min (in any tab).
        // Without this, React strict-mode double-mount + multi-tab sessions
        // burn through the per-IP rate limit and trigger 429 console noise.
        try {
          const REFRESH_KEY = 'tw.lastAuthRefresh';
          const REFRESH_WINDOW_MS = 5 * 60 * 1000;
          const last = Number(sessionStorage.getItem(REFRESH_KEY) || '0');
          if (Date.now() - last < REFRESH_WINDOW_MS) return;
          const refreshRes = await auth.refresh();
          sessionStorage.setItem(REFRESH_KEY, String(Date.now()));
          if (refreshRes.data?.access_token) {
            setToken(refreshRes.data.access_token);
            setAuthToken(refreshRes.data.access_token);
          }
        } catch {
          // Token refresh failed — WS won't work but API calls still use the
          // persisted token from localStorage if cookies are blocked.
        }
      })
      .catch(() => {
        setUser(null);
        setAuthToken(null);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []); // eslint-disable-line -- Intentional: run only on mount

  const login = async (email: string, password: string): Promise<User> => {
    let res;
    try {
      res = await auth.login({ email, password });
    } catch (err: any) {
      // Retry once on network failure (e.g. Fly.io cold-start drops the first request)
      if (!err.response) {
        res = await auth.login({ email, password });
      } else {
        throw err;
      }
    }
    const { access_token, user: userData } = res.data;
    // Keep token in memory for WebSocket auth + persist for Safari fallback
    setToken(access_token);
    setAuthToken(access_token);
    setUser(userData);
    return userData;
  };

  const register = async (name: string, email: string, password: string): Promise<User> => {
    const res = await auth.register({ name, email, password });
    const { access_token, user: userData } = res.data;
    setToken(access_token);
    setAuthToken(access_token);
    setUser(userData);
    return userData;
  };

  const logout = async (): Promise<void> => {
    try {
      await auth.logout();
    } catch {
      // Clear state even if server call fails
    }
    setToken(null);
    setAuthToken(null);
    setUser(null);
    window.location.href = '/login';
  };

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      isAuthenticated: !!user,
      currentOrgId,
      setCurrentOrgId,
      login,
      register,
      logout,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

export default AuthContext;
